import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router';
import type { MealPlanDto } from '@fewerbags/schema';
import { api, queryKeys } from '../../lib/api';
import { addDays, combineDateTime, formatClock, todayIso } from '../../lib/planDates';
import { planTitle, SLOT_LABEL, SLOT_ORDER } from '../../lib/plans';
import { buttonClass } from '../Button';
import { ClockIcon, FlameIcon, PlusIcon } from '../icons';
import { GroceriesIcon, PlanIcon, RecipesIcon } from '../NavIcons';
import { RecipePhoto } from '../recipes/RecipePhoto';

/** Same window as UpcomingMealsCard so both read one cached query. */
const DAYS_AHEAD = 6;

function greeting(now: Date): string {
  const h = now.getHours();
  if (h < 5) return 'Good evening';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

/** Today's main meal: dinner if planned, otherwise the latest non-skipped slot today. */
export function pickTonight(plans: readonly MealPlanDto[], today: string): MealPlanDto | null {
  const todays = plans.filter((p) => p.date === today && p.status !== 'skipped');
  if (todays.length === 0) return null;
  const dinner = todays.find((p) => p.slot === 'dinner');
  if (dinner) return dinner;
  return (
    [...todays].sort((a, b) => SLOT_ORDER.indexOf(b.slot) - SLOT_ORDER.indexOf(a.slot))[0] ?? null
  );
}

function TonightPanel({ plan }: { plan: MealPlanDto }) {
  const hero = plan.recipes.find((r) => r.photoMediaId) ?? plan.recipes[0];
  const serveIso = combineDateTime(plan.date, plan.serveAt);
  // Start time comes from the merged timeline (same query/key as the plan page).
  const timeline = useQuery({
    queryKey: queryKeys.plans.timeline(plan.id),
    queryFn: () => api.plans.timeline(plan.id),
    enabled: plan.recipes.length > 0 && plan.status !== 'cooked',
  });
  const startAt = timeline.data?.startAt;
  const label =
    plan.slot === 'dinner' ? 'Tonight’s dinner' : `Today’s ${SLOT_LABEL[plan.slot].toLowerCase()}`;

  return (
    <div className="flex flex-col overflow-hidden rounded-2xl bg-surface text-ink shadow-[var(--shadow-lift)] sm:flex-row">
      <Link
        to={`/plan/${plan.id}`}
        tabIndex={-1}
        aria-hidden
        className="relative block shrink-0 sm:w-56 lg:w-64"
      >
        {hero ? (
          <RecipePhoto
            photoMediaId={hero.photoMediaId}
            title={hero.title}
            seed={hero.title}
            variant="full"
            className="h-40 w-full sm:h-full sm:min-h-44"
          />
        ) : (
          <div className="flex h-40 items-center justify-center bg-brand-soft sm:h-full">
            <PlanIcon className="size-14" />
          </div>
        )}
      </Link>
      <div className="flex min-w-0 flex-1 flex-col gap-3 p-4 sm:p-5">
        <p className="text-xs font-semibold tracking-wide text-orange-ink uppercase">{label}</p>
        <h2 className="text-xl leading-tight">
          <Link to={`/plan/${plan.id}`} className="hover:underline">
            {planTitle(plan)}
          </Link>
        </h2>
        <dl className="flex flex-wrap gap-2 text-sm">
          {serveIso ? (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-brand-soft px-3 py-1 text-brand">
              <ClockIcon className="size-4" />
              <dt className="sr-only">Serve at</dt>
              <dd>
                Serve <span className="font-semibold">{formatClock(serveIso)}</span>
              </dd>
            </div>
          ) : null}
          {startAt ? (
            <div className="inline-flex items-center gap-1.5 rounded-full bg-orange-soft px-3 py-1 text-orange-ink">
              <FlameIcon className="size-4" />
              <dt className="sr-only">Start cooking</dt>
              <dd>
                Start <span className="font-semibold">{formatClock(startAt)}</span>
              </dd>
            </div>
          ) : null}
          {plan.status === 'cooked' ? (
            <div className="inline-flex items-center rounded-full bg-accent-soft px-3 py-1 text-accent-ink">
              <dt className="sr-only">Status</dt>
              <dd className="font-semibold">Cooked</dd>
            </div>
          ) : null}
        </dl>
        <div className="mt-auto flex flex-wrap gap-2">
          {plan.recipes.length > 0 && plan.status !== 'cooked' ? (
            <Link
              to={`/plan/${plan.id}/cook`}
              className={buttonClass({ variant: 'primary', size: 'sm', block: false })}
            >
              <FlameIcon className="size-4" /> Start cooking
            </Link>
          ) : null}
          <Link
            to={`/plan/${plan.id}`}
            className={buttonClass({ variant: 'secondary', size: 'sm', block: false })}
          >
            Open plan
          </Link>
        </div>
      </div>
    </div>
  );
}

const QUICK = [
  { to: '/plan/week', label: 'Plan my week', Icon: PlanIcon },
  { to: '/recipes/new', label: 'Add recipe', Icon: RecipesIcon },
  { to: '/groceries', label: 'Groceries', Icon: GroceriesIcon },
] as const;

/**
 * Dashboard hero: a time-of-day greeting on deep teal, tonight's meal (photo,
 * serve time, start-cooking time from the timeline) and quick actions.
 */
export function TonightHero({
  firstName,
  household,
}: {
  firstName: string | null;
  household: string | null;
}) {
  const from = todayIso();
  const to = addDays(from, DAYS_AHEAD);
  const plans = useQuery({
    queryKey: queryKeys.plans.week(from, to),
    queryFn: () => api.plans.list(from, to),
  });
  const tonight = pickTonight(plans.data?.plans ?? [], from);

  return (
    <section
      aria-labelledby="dashboard-greeting"
      className="on-teal relative overflow-hidden rounded-3xl bg-brand p-5 text-white shadow-[var(--shadow-lift)] sm:p-7"
    >
      {/* Decorative brand shapes: a green and an orange disc behind the content. */}
      <span
        aria-hidden
        className="pointer-events-none absolute -top-16 -right-12 size-56 rounded-full bg-accent/25"
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -bottom-20 left-1/3 size-48 rounded-full bg-orange/20"
      />
      <div className="relative flex flex-col gap-5">
        <div>
          <h1 id="dashboard-greeting" className="text-2xl text-white sm:text-3xl">
            {greeting(new Date())}
            {firstName ? `, ${firstName}` : ''}
          </h1>
          <p className="mt-1 text-sm text-white/80">
            {household ? `${household} · ` : ''}
            {new Date().toLocaleDateString(undefined, {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
            })}
          </p>
        </div>

        {plans.isPending ? (
          <div aria-hidden className="h-40 rounded-2xl bg-white/10" />
        ) : tonight ? (
          <TonightPanel plan={tonight} />
        ) : (
          <div className="flex flex-col gap-3 rounded-2xl bg-white/10 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="font-semibold text-white">Nothing planned for tonight yet</p>
              <p className="text-sm text-white/80">
                Pick a recipe now, or let FewerBags suggest a whole week.
              </p>
            </div>
            <Link
              to="/plan"
              className="inline-flex min-h-11 shrink-0 items-center justify-center gap-2 rounded-xl bg-accent px-4 text-sm font-semibold text-ink transition-transform hover:brightness-105 active:scale-[0.98]"
            >
              <PlusIcon className="size-4" /> Plan a meal
            </Link>
          </div>
        )}

        <nav aria-label="Quick actions">
          <ul className="grid grid-cols-3 gap-2 sm:gap-3">
            {QUICK.map(({ to, label, Icon }) => (
              <li key={to}>
                <Link
                  to={to}
                  className="flex min-h-11 flex-col items-center justify-center gap-1.5 rounded-2xl bg-cream px-2 py-3 text-center text-xs font-semibold text-brand transition-[transform,box-shadow] hover:-translate-y-0.5 hover:shadow-md active:scale-[0.98] sm:flex-row sm:gap-2.5 sm:text-sm"
                >
                  <Icon className="size-7" />
                  {label}
                </Link>
              </li>
            ))}
          </ul>
        </nav>
      </div>
    </section>
  );
}
